import { createFileRoute, notFound } from "@tanstack/react-router";
import { CityPage } from "@/components/CityPage";
import { cities } from "@/lib/cities";

const SITE = "https://shopify-freelancer.net";

const findCity = (slug: string) => cities.find((c) => c.slug === slug);

export const Route = createFileRoute("/shopify-freelancer/$city")({
  loader: ({ params }) => {
    const city = findCity(params.city);
    if (!city) throw notFound();
    return { city };
  },
  head: ({ loaderData }) => {
    const city = loaderData?.city ?? findCity("berlin")!;
    const path = `/shopify-freelancer/${city.slug}`;
    const title = `Shopify Freelancer ${city.name} | Shop-Aufbau, Entwicklung, CRO`;
    const description = `Shopify Freelancer für ${city.name}: Shop-Erstellung, Theme-Entwicklung (110 € netto/h) und Conversion-Optimierung. Remote, direkt mit dem, der umsetzt.`;
    const faqs = [
      {
        q: `Arbeiten Sie auch für Kunden in ${city.name}?`,
        a: `Ja. Ich betreue Kunden in ${city.name} und ganz ${city.region} vollständig remote. Abstimmung per E-Mail oder Call, Ergebnisse sehen Sie immer zuerst in einer Testumgebung.`,
      },
      {
        q: "Was kostet ein Shopify Freelancer?",
        a: "Anpassungen und Entwicklung rechne ich mit 110 € netto pro Stunde ab. Conversion-Optimierung startet bei 3.990 € netto. Vor dem Start bekommen Sie eine klare Aufwandsschätzung.",
      },
      {
        q: "Wie schnell kann es losgehen?",
        a: "Nach Ihrer Anfrage melde ich mich innerhalb von 48 Stunden mit einer Einschätzung. Der Starttermin hängt von meiner aktuellen Auslastung ab, das erfahren Sie transparent im ersten Gespräch.",
      },
      {
        q: "Übernehmen Sie auch bestehende Shops?",
        a: "Ja, das ist der häufigste Fall. Ich sehe mir Ihren Shop an und sage Ihnen, welche Anpassungen sich lohnen und welche nicht.",
      },
    ];
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: `${SITE}${path}` },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: `${SITE}${path}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `Shopify Freelancer ${city.name}`,
            provider: { "@type": "Person", name: "Nicolai Schnepf" },
            areaServed: { "@type": "City", name: city.name },
            url: `${SITE}${path}`,
            description,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: CityRoute,
});

function CityRoute() {
  const { city } = Route.useLoaderData();
  return <CityPage city={city} />;
}
